import { type RefObject, useLayoutEffect, useState } from 'react'

/**
 * Which side of its anchor an absolutely positioned popup should open on.
 * Opens below unless the space under the anchor is smaller than `height` and
 * there is more room above. Opening upward keeps the popup from extending the
 * scroll height of the container it sits in, which would otherwise make the
 * browser scroll the surrounding column to reveal the focused search box.
 */
export function usePopupSide(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  height: number,
): 'below' | 'above' {
  return usePopupPlacement(anchorRef, open, height).side
}

const EDGE_MARGIN_PX = 8
const MIN_MAX_HEIGHT_PX = 160

/**
 * `usePopupSide` plus the room available on the chosen side, for popups that
 * must shrink and scroll internally instead of being clipped. `maxHeight` is
 * the room on the chosen side; callers clamp it to their own design cap.
 */
export function usePopupPlacement(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  height: number,
): { side: 'below' | 'above'; maxHeight: number | undefined } {
  const [placement, setPlacement] = useState<{
    side: 'below' | 'above'
    maxHeight: number | undefined
  }>({ side: 'below', maxHeight: undefined })
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    // The room that counts ends at the nearest scrolling ancestor (the card's
    // rail), not the window: a popup that outruns it extends that scroller.
    let top = 0
    let bottom = window.innerHeight
    for (let el = anchorRef.current.parentElement; el; el = el.parentElement) {
      const overflowY = window.getComputedStyle(el).overflowY
      if (overflowY === 'auto' || overflowY === 'scroll') {
        const bounds = el.getBoundingClientRect()
        top = Math.max(top, bounds.top)
        bottom = Math.min(bottom, bounds.bottom)
        break
      }
    }
    const below = bottom - rect.bottom
    const above = rect.top - top
    const side = below < height + EDGE_MARGIN_PX && above > below ? 'above' : 'below'
    const room = side === 'above' ? above : below
    const maxHeight = Math.max(MIN_MAX_HEIGHT_PX, Math.floor(room - EDGE_MARGIN_PX))
    setPlacement((prev) =>
      prev.side === side && prev.maxHeight === maxHeight ? prev : { side, maxHeight },
    )
  }, [open, anchorRef, height])
  return placement
}
